/**
 * Employment capacity derivation utilities.
 *
 * Shared predicates for computing effective weekly hours, employment ratios,
 * and active-employment status from time tracking periods.
 * Used by: validation rules, solver pre-filtering, AI candidate filtering.
 */

/**
 * Minimal time tracking period for capacity calculations.
 */
export interface TimeTrackingPeriodSnapshot {
  id: string;
  workloadHoursPerDay: number;
  startDate: string; // ISO date
  endDate: string | null; // null = ongoing
  rrule?: string | null;
  /** Working-days bitmask (bit 0 = Mon … bit 6 = Sun). Optional for callers that do not track it. */
  workdayMask?: number;
}

/**
 * Derived employment capacity for a given employee in a given context.
 */
export interface EmploymentCapacity {
  /** Hours per day from active period */
  workloadHoursPerDay: number;
  /** Target hours per week = workloadHoursPerDay * workingDaysPerWeek */
  targetHoursPerWeek: number;
  /** Employment ratio = targetHoursPerWeek / fullTimeHoursPerWeek */
  employmentRatio: number;
  /** ID of the source time tracking period */
  sourcePeriodId: string;
}

/** Default full-time baseline when tenant setting is not configured */
export const DEFAULT_FULL_TIME_HOURS_PER_WEEK = 40;

/** Default working days per week (Mon-Fri) */
export const DEFAULT_WORKING_DAYS_PER_WEEK = 5;

/**
 * Count the working days encoded in a workday bitmask (bit 0 = Mon … bit 6 =
 * Sun). Returns the popcount of the low seven bits — e.g. Mon–Fri (`31`) → 5,
 * Mon/Wed/Fri (`21`) → 3.
 */
export function countWorkdayBits(mask: number): number {
  let count = 0;
  for (let bit = 0; bit < 7; bit++) {
    if ((mask & (1 << bit)) !== 0) count++;
  }
  return count;
}

/**
 * Whether the given ISO weekday (`0 = Mon … 6 = Sun`) is a working day in the
 * workday bitmask. The bit index equals the ISO weekday index, so this is a
 * direct bit test. Used to disable a person's non-working day cells in the
 * planning grid.
 */
export function isWorkdayInMask(mask: number, isoWeekday: number): boolean {
  if (isoWeekday < 0 || isoWeekday > 6) return false;
  return (mask & (1 << isoWeekday)) !== 0;
}

/** Workday bitmask with every weekday (Mon–Sun) set. */
export const FULL_WEEK_WORKDAY_MASK = 0b1111111;

/**
 * Whether `date` falls within `[start, end]` (inclusive). `end === null` means open-ended.
 * The one shared primitive behind both `findActivePeriod` and any other "was X active on
 * date Y" check — including the plan-grid drag-and-drop employment check — so they can't
 * silently drift into different date-boundary semantics.
 */
export function isDateInRange(date: string, start: string, end: string | null): boolean {
  return start <= date && (end === null || end >= date);
}

/**
 * Find the active time tracking period for a given date.
 * A period is active if startDate <= date AND (endDate is null OR endDate >= date).
 *
 * @returns The active period or undefined if none found
 */
export function findActivePeriod(periods: TimeTrackingPeriodSnapshot[], date: string): TimeTrackingPeriodSnapshot | undefined {
  return periods.find(period => isDateInRange(date, period.startDate, period.endDate));
}

/**
 * Return all time tracking periods whose date range overlaps the inclusive
 * window `[fromDate, toDate]`. A period overlaps when its start is on or before
 * `toDate` and its end is `null` (open-ended) or on or after `fromDate`.
 */
export function findPeriodsOverlappingRange(
  periods: TimeTrackingPeriodSnapshot[],
  fromDate: string,
  toDate: string
): TimeTrackingPeriodSnapshot[] {
  return periods.filter(period => period.startDate <= toDate && (period.endDate === null || period.endDate >= fromDate));
}

/**
 * Resolve the effective working-days bitmask for an employee across a date
 * range that may span multiple time-tracking periods.
 *
 * The mask is the intersection (bitwise AND) of every period that overlaps
 * `[fromDate, toDate]`: an employee is only considered to work a given
 * weekday when that weekday is a working day in every period covering the
 * window. This is the conservative choice — auto-fill and conflict resolution
 * never place someone on a weekday they don't work in some part of the
 * planning horizon.
 *
 * When no period (or no period with a defined mask) overlaps the range, the
 * caller has no contractual signal — fall back to `FULL_WEEK_WORKDAY_MASK` so
 * the solver is not over-restricted.
 */
export function intersectWorkdayMasksForRange(periods: TimeTrackingPeriodSnapshot[], fromDate: string, toDate: string): number {
  const overlapping = findPeriodsOverlappingRange(periods, fromDate, toDate).filter(
    (period): period is TimeTrackingPeriodSnapshot & { workdayMask: number } => period.workdayMask !== undefined
  );
  if (overlapping.length === 0) return FULL_WEEK_WORKDAY_MASK;
  return overlapping.reduce((acc, period) => acc & period.workdayMask, FULL_WEEK_WORKDAY_MASK);
}

/**
 * Check if an employee has an active employment period on a given date.
 */
export function isEmployeeActiveOnDate(periods: TimeTrackingPeriodSnapshot[], date: string): boolean {
  return findActivePeriod(periods, date) !== undefined;
}

/**
 * Derive employment capacity from an active time tracking period.
 *
 * @param period - The active time tracking period
 * @param workingDaysPerWeek - Number of working days (from strategy), default 5
 * @param fullTimeHoursPerWeek - Full-time baseline (from tenant settings), default 40
 * @returns Derived capacity values
 */
export function deriveEmploymentCapacity(
  period: TimeTrackingPeriodSnapshot,
  workingDaysPerWeek = DEFAULT_WORKING_DAYS_PER_WEEK,
  fullTimeHoursPerWeek = DEFAULT_FULL_TIME_HOURS_PER_WEEK
): EmploymentCapacity {
  const targetHoursPerWeek = period.workloadHoursPerDay * workingDaysPerWeek;
  const employmentRatio = fullTimeHoursPerWeek > 0 ? targetHoursPerWeek / fullTimeHoursPerWeek : 1;

  return {
    workloadHoursPerDay: period.workloadHoursPerDay,
    targetHoursPerWeek,
    employmentRatio: Math.round(employmentRatio * 100) / 100,
    sourcePeriodId: period.id,
  };
}

/**
 * Get employment capacity for an employee, returning fallback values
 * when no active period exists (template mode uses full-time defaults).
 *
 * @returns EmploymentCapacity or null if no period and no fallback desired
 */
export function getEmploymentCapacity(
  periods: TimeTrackingPeriodSnapshot[],
  date: string,
  workingDaysPerWeek = DEFAULT_WORKING_DAYS_PER_WEEK,
  fullTimeHoursPerWeek = DEFAULT_FULL_TIME_HOURS_PER_WEEK
): EmploymentCapacity | null {
  const activePeriod = findActivePeriod(periods, date);
  if (!activePeriod) return null;

  return deriveEmploymentCapacity(activePeriod, workingDaysPerWeek, fullTimeHoursPerWeek);
}

/**
 * Get employment capacity with fallback for template mode.
 * When no active period exists, returns full-time default values.
 */
export function getEmploymentCapacityWithFallback(
  periods: TimeTrackingPeriodSnapshot[],
  date: string,
  workingDaysPerWeek = DEFAULT_WORKING_DAYS_PER_WEEK,
  fullTimeHoursPerWeek = DEFAULT_FULL_TIME_HOURS_PER_WEEK
): EmploymentCapacity {
  const capacity = getEmploymentCapacity(periods, date, workingDaysPerWeek, fullTimeHoursPerWeek);
  if (capacity) return capacity;

  // Fallback: assume full-time
  const dailyHours = fullTimeHoursPerWeek / workingDaysPerWeek;
  return {
    workloadHoursPerDay: dailyHours,
    targetHoursPerWeek: fullTimeHoursPerWeek,
    employmentRatio: 1,
    sourcePeriodId: '',
  };
}

/**
 * Check if assigning additional hours would exceed the weekly cap.
 *
 * @param alreadyPlannedHours - Hours already planned for the employee this week
 * @param additionalHours - Hours to add (e.g., shift duration)
 * @param targetHoursPerWeek - Employee's weekly cap
 * @returns true if assigning would exceed the cap
 */
export function wouldExceedWeeklyCap(alreadyPlannedHours: number, additionalHours: number, targetHoursPerWeek: number): boolean {
  return alreadyPlannedHours + additionalHours > targetHoursPerWeek;
}

/**
 * Sum an employee's target hours across a set of weeks, resolving each
 * week's own correct capacity from their raw employment periods instead of
 * multiplying a single request-wide snapshot by the week count.
 *
 * See docs/specs/per-week-employment-capacity-resolution.md — this fixes the
 * planning grid's summary cells the same way the solver was already fixed:
 * a multi-week view spanning an employment-rate change previously showed one
 * constant value (the rate active at the start of the range) for every week.
 *
 * Falls back to `fallbackTargetHoursPerWeek * weekStartDates.length` when no
 * periods data is available, preserving prior behaviour for callers that
 * haven't been updated to pass periods yet.
 */
export function sumTargetHoursAcrossWeeks(
  periods: TimeTrackingPeriodSnapshot[] | undefined,
  weekStartDates: readonly string[],
  fullTimeHoursPerWeek: number,
  fallbackTargetHoursPerWeek: number
): number {
  if (!periods || periods.length === 0) {
    return fallbackTargetHoursPerWeek * weekStartDates.length;
  }

  return weekStartDates.reduce((sum, weekStart) => {
    const activePeriod = findActivePeriod(periods, weekStart);
    const workingDaysPerWeek =
      activePeriod?.workdayMask !== undefined
        ? countWorkdayBits(activePeriod.workdayMask) || DEFAULT_WORKING_DAYS_PER_WEEK
        : DEFAULT_WORKING_DAYS_PER_WEEK;
    return sum + getEmploymentCapacityWithFallback(periods, weekStart, workingDaysPerWeek, fullTimeHoursPerWeek).targetHoursPerWeek;
  }, 0);
}
